import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import BusinessIcon from "@mui/icons-material/Business";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import useStyles from "./styles";
import { AttachUserDialog } from "./AttachUserDialog";
import {
  useExpireStaleInvitations,
  useOnboardingIssues,
  useResendInvitation,
} from "../../api/onboarding";

/**
 * Accueil — santé du parcours d'entrée des clients.
 *
 * POURQUOI CET ECRAN EXISTE
 * -------------------------
 * Deux clients sont restés bloqués dehors la même semaine. Dans les deux cas
 * l'information était en base dès la première minute ; il manquait un endroit
 * pour la regarder. Le diagnostic a demandé une session SSH et cinq requêtes
 * écrites à la main sur la production, la réparation un INSERT direct.
 *
 * Cet écran répond à deux questions, et à rien d'autre :
 *   « qui est bloqué dehors en ce moment »
 *   « comment le débloquer sans ouvrir un client SQL »
 *
 * LES QUATRE ONGLETS sont ordonnés par urgence décroissante, pas par volume.
 * Une invitation bloquée décrit toujours quelqu'un qui a essayé d'entrer et
 * n'a pas pu — c'est un client mécontent qui n'a pas encore appelé. Un compte
 * orphelin est le plus souvent une inscription abandonnée. Le premier onglet
 * est donc celui qui compte, même s'il est presque toujours vide.
 */
type Props = {};

/** Format court, lisible en tableau. Les dates arrivent en ISO ou en SQL. */
const shortDate = (value?: string | null): string => {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const Onboarding: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [tab, setTab] = useState(0);
  const [attachTarget, setAttachTarget] = useState<{
    user: { user_id: number; email: string };
    companyId?: number | null;
    role?: "FLEET_MANAGER" | "EMPLOYEE";
  } | null>(null);

  const { issues, summary, isLoading, refetch, isFetching } =
    useOnboardingIssues();
  const { mutate: resend, isPending: isResending } = useResendInvitation();
  const { mutate: expireStale, isPending: isExpiring } =
    useExpireStaleInvitations();

  const kpis = [
    {
      label: t("onboarding.kpiStuck", "Invitations bloquées"),
      value: summary?.stuck_invitations ?? 0,
      // Le seul compteur qui passe au rouge : il décrit toujours une personne
      // qui a essayé d'entrer et n'a pas pu.
      color: (summary?.stuck_invitations ?? 0) > 0 ? "error.main" : "text.primary",
      onClick: () => setTab(0),
      hint: t(
        "onboarding.kpiStuckHint",
        "Le compte existe et est actif, mais n'est rattaché à aucune société"
      ),
    },
    {
      label: t("onboarding.kpiExpired", "Invitations expirées"),
      value: summary?.expired_invitations ?? 0,
      color:
        (summary?.expired_invitations ?? 0) > 0 ? "warning.main" : "text.primary",
      onClick: () => setTab(1),
      hint: t(
        "onboarding.kpiExpiredHint",
        "Lien jamais ouvert, fenêtre de 72 h dépassée"
      ),
    },
    {
      label: t("onboarding.kpiNoManager", "Flottes sans gestionnaire"),
      value: summary?.fleets_without_manager ?? 0,
      color:
        (summary?.fleets_without_manager ?? 0) > 0
          ? "warning.main"
          : "text.primary",
      onClick: () => setTab(2),
      hint: t(
        "onboarding.kpiNoManagerHint",
        "Société de flotte active que personne ne peut administrer"
      ),
    },
    {
      label: t("onboarding.kpiOrphans", "Comptes sans société"),
      value: summary?.orphan_users ?? 0,
      color: "text.primary",
      onClick: () => setTab(3),
      hint: t(
        "onboarding.kpiOrphansHint",
        "Souvent des inscriptions abandonnées — à lire, pas à purger"
      ),
    },
    {
      label: t("onboarding.kpiGhosts", "Comptes fantômes"),
      value: summary?.ghost_accounts ?? 0,
      /*
       * Rouge des qu'il y en a un ACTIF, sinon neutre — mais le compteur ne
       * distingue pas les deux, donc on reste sur l'orange : la liste, elle,
       * marque les actifs. Un fantome inactif est inerte ; un fantome actif est
       * l'impasse d'origine, celle ou l'ecran d'invitation renvoyait vers une
       * page de connexion quelqu'un qui n'avait aucun mot de passe.
       */
      color: (summary?.ghost_accounts ?? 0) > 0 ? "warning.main" : "text.primary",
      onClick: () => setTab(4),
      hint: t(
        "onboarding.kpiGhostsHint",
        "Compte sans mot de passe choisi par son propriétaire, et sans société"
      ),
    },
  ];

  const empty = (message: string, allGood = true) => (
    <div className={classes.emptyState}>
      {allGood && (
        <div className={classes.emptyStateOk}>
          {t("onboarding.allClear", "Rien à signaler")}
        </div>
      )}
      {message}
    </div>
  );

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.onboardingRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <div className={classes.containerHeader}>
        <div className={classes.divIconArrow}>
          <HealthAndSafetyIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <CustomTypography
            variant="h2"
            text={t("onboarding.title", "Accueil")}
            isUppercase
          />
        </div>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
          size="small"
        >
          {t("common.refresh", "Actualiser")}
        </Button>
      </div>

      {/* ── Compteurs ─────────────────────────────────────────────────────── */}
      <div className={classes.statsRow}>
        {kpis.map((kpi) => (
          <Tooltip key={kpi.label} title={kpi.hint as string} arrow>
            <Card
              className={classes.statCard}
              onClick={kpi.onClick}
              sx={{ cursor: "pointer" }}
            >
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Box className={classes.statValue} sx={{ color: kpi.color }}>
                  {Number(kpi.value).toLocaleString("fr-FR")}
                </Box>
                <Box className={classes.statLabel}>{kpi.label}</Box>
              </CardContent>
            </Card>
          </Tooltip>
        ))}
      </div>

      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            className={classes.tabsBar}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              icon={
                <Badge
                  color="error"
                  badgeContent={summary?.stuck_invitations ?? 0}
                  max={99}
                  invisible={!summary?.stuck_invitations}
                >
                  <LinkOffIcon />
                </Badge>
              }
              iconPosition="start"
              label={t("onboarding.tabStuck", "Bloquées")}
            />
            <Tab
              icon={<HourglassBottomIcon />}
              iconPosition="start"
              label={t("onboarding.tabExpired", "Expirées")}
            />
            <Tab
              icon={<BusinessIcon />}
              iconPosition="start"
              label={t("onboarding.tabNoManager", "Sans gestionnaire")}
            />
            <Tab
              icon={<PersonOffIcon />}
              iconPosition="start"
              label={t("onboarding.tabOrphans", "Sans société")}
            />
            <Tab
              icon={
                <Badge
                  color="warning"
                  badgeContent={summary?.ghost_accounts ?? 0}
                  max={99}
                  invisible={!summary?.ghost_accounts}
                >
                  <NoAccountsIcon />
                </Badge>
              }
              iconPosition="start"
              label={t("onboarding.tabGhosts", "Fantômes")}
            />
          </Tabs>

          {isLoading && (
            <div className={classes.emptyState}>
              <CircularProgress size={24} />
            </div>
          )}

          {/* ── 0. Invitations bloquées ───────────────────────────────────── */}
          {!isLoading && tab === 0 && (
            <>
              <div className={classes.hint}>
                {t(
                  "onboarding.hintStuck",
                  "Ces personnes ont un compte actif et une invitation en attente, mais aucun rattachement : elles se connectent et l'application leur est refusée. Depuis le correctif, le rattachement se fait automatiquement à leur prochaine connexion — « Rattacher » permet de ne pas attendre."
                )}
              </div>
              {issues?.stuck_invitations?.length ? (
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("onboarding.colEmail", "E-mail")}</TableCell>
                        <TableCell>{t("onboarding.colCompany", "Société")}</TableCell>
                        <TableCell>{t("onboarding.colRole", "Rôle")}</TableCell>
                        <TableCell>{t("onboarding.colInvited", "Invité le")}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issues.stuck_invitations.map((row) => (
                        <TableRow key={row.invitation_code} hover>
                          <TableCell className={classes.mono}>{row.email}</TableCell>
                          <TableCell>
                            {row.company_name}
                            <div className={classes.monoSmall}>#{row.company_id}</div>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.role}
                              color={
                                row.role === "FLEET_MANAGER" ? "primary" : "default"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {shortDate(row.invited_at)}
                            {row.is_expired && (
                              <div className={classes.monoSmall}>
                                {t("onboarding.expiredMark", "invitation expirée")}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className={classes.actionsCell}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  setAttachTarget({
                                    user: { user_id: row.user_id, email: row.email },
                                    companyId: row.company_id,
                                    role:
                                      row.role === "EMPLOYEE"
                                        ? "EMPLOYEE"
                                        : "FLEET_MANAGER",
                                  })
                                }
                              >
                                {t("onboarding.attach", "Rattacher")}
                              </Button>
                              <Button
                                size="small"
                                disabled={isResending}
                                onClick={() =>
                                  resend({
                                    companyId: row.company_id,
                                    invitationCode: row.invitation_code,
                                  })
                                }
                              >
                                {t("onboarding.resend", "Renvoyer")}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                empty(
                  t(
                    "onboarding.emptyStuck",
                    "Personne n'est bloqué à l'entrée en ce moment."
                  )
                )
              )}
            </>
          )}

          {/* ── 1. Invitations expirées ───────────────────────────────────── */}
          {!isLoading && tab === 1 && (
            <>
              <div className={classes.hint}>
                {t(
                  "onboarding.hintExpired",
                  "Invitations jamais ouvertes dont la fenêtre de 72 h est passée. La veille quotidienne les bascule en « expirée » chaque matin ; « Renvoyer » relance le lien avec une nouvelle fenêtre."
                )}
              </div>
              <Box sx={{ mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isExpiring}
                  onClick={() => expireStale()}
                >
                  {t(
                    "onboarding.expireStale",
                    "Marquer les dépassées comme expirées"
                  )}
                </Button>
              </Box>
              {issues?.expired_invitations?.length ? (
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("onboarding.colEmail", "E-mail")}</TableCell>
                        <TableCell>{t("onboarding.colCompany", "Société")}</TableCell>
                        <TableCell>{t("onboarding.colRole", "Rôle")}</TableCell>
                        <TableCell>{t("onboarding.colExpired", "Expirée le")}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issues.expired_invitations.map((row) => (
                        <TableRow key={row.invitation_code} hover>
                          <TableCell className={classes.mono}>{row.email}</TableCell>
                          <TableCell>
                            {row.company_name}
                            <div className={classes.monoSmall}>#{row.company_id}</div>
                          </TableCell>
                          <TableCell>{row.role}</TableCell>
                          <TableCell>{shortDate(row.expires_at)}</TableCell>
                          <TableCell>
                            <div className={classes.actionsCell}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={isResending}
                                onClick={() =>
                                  resend({
                                    companyId: row.company_id,
                                    invitationCode: row.invitation_code,
                                  })
                                }
                              >
                                {t("onboarding.resend", "Renvoyer")}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                empty(
                  t(
                    "onboarding.emptyExpired",
                    "Aucune invitation en attente n'a dépassé sa fenêtre."
                  )
                )
              )}
            </>
          )}

          {/* ── 2. Flottes sans gestionnaire ──────────────────────────────── */}
          {!isLoading && tab === 2 && (
            <>
              <div className={classes.hint}>
                {t(
                  "onboarding.hintNoManager",
                  "Sociétés de flotte actives sans aucun gestionnaire actif : personne ne peut administrer ces clients. Une invitation en attente signifie que l'accueil est en cours, pas qu'il est abandonné."
                )}
              </div>
              {issues?.fleets_without_manager?.length ? (
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("onboarding.colCompany", "Société")}</TableCell>
                        <TableCell>{t("onboarding.colCreated", "Créée le")}</TableCell>
                        <TableCell>
                          {t("onboarding.colPending", "Invitations en attente")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issues.fleets_without_manager.map((row) => (
                        <TableRow key={row.company_id} hover>
                          <TableCell>
                            {row.company_name}
                            <div className={classes.monoSmall}>#{row.company_id}</div>
                          </TableCell>
                          <TableCell>{shortDate(row.created_at)}</TableCell>
                          <TableCell>
                            {row.pending_invitations > 0 ? (
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={t(
                                  "onboarding.pendingCount",
                                  "{{count}} en attente",
                                  { count: row.pending_invitations }
                                )}
                              />
                            ) : (
                              <Chip
                                size="small"
                                color="warning"
                                label={t("onboarding.noneSent", "aucune envoyée")}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                empty(
                  t(
                    "onboarding.emptyNoManager",
                    "Chaque flotte active a au moins un gestionnaire."
                  )
                )
              )}
            </>
          )}

          {/* ── 3. Comptes sans société ───────────────────────────────────── */}
          {!isLoading && tab === 3 && (
            <>
              <div className={classes.hint}>
                {t(
                  "onboarding.hintOrphans",
                  "Comptes actifs sans aucun rattachement. La plupart sont des inscriptions abandonnées : ne rien purger sur la seule foi de cette liste. Un compte dont l'adresse est vérifiée mais qui n'ouvre aucune société mérite un coup d'œil."
                )}
              </div>
              {issues?.orphan_users?.length ? (
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("onboarding.colEmail", "E-mail")}</TableCell>
                        <TableCell>{t("onboarding.colName", "Nom")}</TableCell>
                        <TableCell>{t("onboarding.colCreated", "Créé le")}</TableCell>
                        <TableCell>{t("onboarding.colVerified", "Adresse")}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issues.orphan_users.map((row) => (
                        <TableRow key={row.user_id} hover>
                          <TableCell className={classes.mono}>{row.email}</TableCell>
                          <TableCell>{row.name ?? "—"}</TableCell>
                          <TableCell>{shortDate(row.created_at)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={row.email_verified ? "success" : "default"}
                              label={
                                row.email_verified
                                  ? t("onboarding.verified", "vérifiée")
                                  : t("onboarding.unverified", "non vérifiée")
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className={classes.actionsCell}>
                              <Button
                                size="small"
                                onClick={() =>
                                  setAttachTarget({
                                    user: {
                                      user_id: row.user_id,
                                      email: row.email,
                                    },
                                  })
                                }
                              >
                                {t("onboarding.attach", "Rattacher")}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                empty(
                  t(
                    "onboarding.emptyOrphans",
                    "Tous les comptes actifs sont rattachés à une société."
                  )
                )
              )}
            </>
          )}

          {/* ── 4. Comptes fantômes ───────────────────────────────────── */}
          {!isLoading && tab === 4 && (
            <>
              <div className={classes.hint}>
                {t(
                  "onboarding.hintGhosts",
                  "Comptes dont personne n'a jamais choisi le mot de passe, et qui n'ouvrent aucune société. Ce sont les pré-comptes que l'API créait pour chaque invitation de gestionnaire de flotte avant d'arrêter de le faire. Ils occupent l'adresse de leur propriétaire : tant qu'ils sont actifs, l'écran d'invitation les prend pour des comptes utilisables et renvoie la personne vers une connexion impossible. « Rattacher » règle le cas légitime ; les autres se suppriment à la main, jamais en masse."
                )}
              </div>
              {issues?.ghost_accounts?.length ? (
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("onboarding.colEmail", "E-mail")}</TableCell>
                        <TableCell>{t("onboarding.colName", "Nom")}</TableCell>
                        <TableCell>{t("onboarding.colCreated", "Créé le")}</TableCell>
                        <TableCell>{t("onboarding.colState", "État")}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issues.ghost_accounts.map((row) => (
                        <TableRow key={row.user_id} hover>
                          <TableCell className={classes.mono}>{row.email}</TableCell>
                          <TableCell>{row.name ?? "—"}</TableCell>
                          <TableCell>{shortDate(row.created_at)}</TableCell>
                          <TableCell>
                            {/*
                              Actif = le cas qui cassait l'accueil. Inactif = une
                              ligne inerte, sans consequence tant qu'on n'y touche
                              pas. La distinction decide de l'urgence, elle merite
                              donc d'etre lisible d'un coup d'oeil.
                            */}
                            <Chip
                              size="small"
                              variant="outlined"
                              color={row.is_active ? "warning" : "default"}
                              label={
                                row.is_active
                                  ? t("onboarding.ghostActive", "actif sans mot de passe")
                                  : t("onboarding.ghostInactive", "inactif")
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className={classes.actionsCell}>
                              <Button
                                size="small"
                                onClick={() =>
                                  setAttachTarget({
                                    user: {
                                      user_id: row.user_id,
                                      email: row.email,
                                    },
                                  })
                                }
                              >
                                {t("onboarding.attach", "Rattacher")}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                empty(
                  t(
                    "onboarding.emptyGhosts",
                    "Aucun compte sans mot de passe en attente."
                  )
                )
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AttachUserDialog
        open={!!attachTarget}
        onClose={() => setAttachTarget(null)}
        user={attachTarget?.user ?? null}
        defaultCompanyId={attachTarget?.companyId ?? null}
        defaultRole={attachTarget?.role}
      />
    </Sidebar>
  );
};
